import {AxiosRequestConfig} from 'axios';

import {
    URL_REQUESTS_ACTION,
    URL_REQUESTS_DELETE,
    URL_REQUESTS_READ,
    URL_REQUESTS_GET_REQUEST_ACTION_STATUS,
    URL_REQUESTS_RUN_REQUEST_ACTION,
    URL_REQUESTS_STATUS_COMMENT_ACTION,
    URL_REQUEST_RATING,
    URL_REQUEST_UPDATE_DOC_JOURNAL,
} from 'shared/constants/urls';
import {ERequestType} from 'shared/types/requests';
import {convertToFormData, performRequest} from 'shared/utils';

import {IRequestData, ERequestStatus} from './request-slice-type';

export type TFetchRequestArgs = {
  id: string;
  url: string;
};
export const fetchRequestData = ({
    id,
    url,
}: TFetchRequestArgs) => (
    performRequest<IRequestData>({
        url: `${url}/${id}`,
        method: ERequestType.GET,
    })
);

export type TRatingData = {
    commentId: string;
    rating: number;
    url?: string;
};

export const toggleRating = async ({commentId, rating, url}: TRatingData) => performRequest({
    url: url ?? URL_REQUEST_RATING,
    method: ERequestType.POST,
    data: convertToFormData({
        commentId,
        rating,
    }),
});

export type TPostRequestArgs = {
    commentId: string;
};
export const postRequestStatus = ({commentId}: TPostRequestArgs) => performRequest<ERequestStatus[]>({
    url: URL_REQUESTS_ACTION,
    method: ERequestType.POST,
    params: {commentId},
});

export const postRequestCommentStatus = ({commentId}: TPostRequestArgs) => performRequest<ERequestStatus[]>({
    url: URL_REQUESTS_STATUS_COMMENT_ACTION,
    method: ERequestType.POST,
    params: {commentId},
});

export const markRequestRead = (ids: string[]) => performRequest({
    url: URL_REQUESTS_READ,
    method: ERequestType.POST,
    data: {ids},
});

export const updateDocJournal = ({requestId}: {requestId: string}) => performRequest<string>({
    url: URL_REQUEST_UPDATE_DOC_JOURNAL,
    method: ERequestType.POST,
    params: {requestId},
});

export const removeRequestData = ({commentId}: TPostRequestArgs) => performRequest<string>({
    url: URL_REQUESTS_DELETE,
    method: ERequestType.POST,
    params: {commentId},
});

export type TPostRequestDataArgs = {
    url: string;
    commentId: string;
    data: any;
};
export const postRequestData = (
    {url, commentId, data}: TPostRequestDataArgs,
) => performRequest({
    url: `${url}`,
    method: 'POST',
    params: {commentId},
    data: convertToFormData(data, {shouldUsedFileIndexName: true}),
});

export interface IGetActionStatusByIdAndActionCodeRequestArgs {
    actionCode: string;
    topicId: string;
}
export const getActionStatusByIdAndActionCodeRequest = (
    args: IGetActionStatusByIdAndActionCodeRequestArgs,
) => performRequest({
    url: URL_REQUESTS_GET_REQUEST_ACTION_STATUS,
    method: 'POST',
    data: convertToFormData({...args}),
});

export interface IRunActionByIdAndActionCodeRequestArgs {
    actionCode: string;
    topicId: string;
}
export const runActionByIdAndActionCodeRequest = (
    args: IGetActionStatusByIdAndActionCodeRequestArgs,
) => performRequest({
    url: URL_REQUESTS_RUN_REQUEST_ACTION,
    method: 'POST',
    data: convertToFormData({...args}),
});

export const createRequestStatus = () => {
    const createPost = (
        isJsonRequest?: boolean,
    ) => (params: AxiosRequestConfig['params'], referenceUrl: string, data?: any) => {
        if (!isJsonRequest) {
            return (
                performRequest({
                    url: `${referenceUrl}`,
                    method: ERequestType.POST,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    params: {...params},
                    data,
                })
            );
        }
        return (
            performRequest({
                url: `/${referenceUrl}`,
                method: ERequestType.POST,
                params: {...params},
                data: data && convertToFormData(data),
            })
        );
    };

    const apiList = {
        [ERequestStatus.CLOSE_REQUEST]: createPost(),
        [ERequestStatus.TAKE_TO_WORK_REQUEST]: createPost(),
        [ERequestStatus.TAKE_TO_WORK_REQUEST_NOT_DATE]: createPost(false),
        [ERequestStatus.ASSIGN_RESPONSIBLE]: createPost(),
        [ERequestStatus.REQUEST_PROLONGATION]: createPost(true),
        [ERequestStatus.GENERATE_RESPONSE]: createPost(true),
        [ERequestStatus.COMMENT_REQUEST]: createPost(true),
        [ERequestStatus.BUTTON_CONTROL_COLLAPSE]: () => null,
        [ERequestStatus.REQUEST_RESPONSE]: () => null,
        [ERequestStatus.REPLY_REQUEST]: createPost(),
        [ERequestStatus.SEND_FOR_APPROVAL_REQUEST]: createPost(),
        [ERequestStatus.REQUEST_RESPONSE_SEND]: () => null,
        [ERequestStatus.PROLONGATION_REQUEST]: createPost(),
        [ERequestStatus.EDIT_RESPONSE]: () => null,
        [ERequestStatus.SEND_MESSAGE]: createPost(),
        [ERequestStatus.PROLONGATION_RESPONSE]: createPost(true),
        [ERequestStatus.DELETE_REQUEST]: createPost(),
        [ERequestStatus.APPROVE_REQUEST]: createPost(),
        [ERequestStatus.REJECT]: createPost(),
        [ERequestStatus.REJECT_REQUEST]: createPost(),
        [ERequestStatus.GENERATE_REJECTION_RESPONSE]: createPost(true),
        [ERequestStatus.GENERATE_ADD_PLACEMENT_DOC_RESPONSE]: createPost(true),
        [ERequestStatus.RATE_REQUEST]: () => null,
        [ERequestStatus.FINALLY_CLOSED]: () => null,
        [ERequestStatus.REQUEST_FROM_D365]: createPost(),
        [ERequestStatus.UPDATE_DOC_JOURNAL]: () => null,
    };

    return {
        changeStatus: (
            newStatus: ERequestStatus,
            commentId: number | string,
            url: string,
            data?: any,
        ) => apiList[newStatus]({commentId}, url, data),
    };
};
